/**
 * **A13 · The will-call slip and the ride-bracelet envelope label** (Stream A, Paper.dc.html):
 * 6×4 cards for an order at the ticket window, from the window's own lookup. The QR is the
 * order's code, which the window reads back; nothing is typed that the order already knows.
 */
import type { ReactNode } from 'react';
import { formatDate } from '@cider/ui';
import { useLoad } from '../views/admin/shared.js';
import { usePrinted } from './printed.js';
import { SheetPages } from './sheets.js';

interface Found {
  rows: {
    orderId: string;
    code: string;
    buyer: string;
    paid: boolean;
    units: { code: string; what: string; forName: string | null; redeemed: string | null }[];
  }[];
}

export function WindowSlip({
  kind,
  code,
}: {
  kind: 'will-call' | 'bracelet-label';
  code: string;
}): ReactNode {
  const d = useLoad<Found>(`/commerce/ticket-window/find?q=${encodeURIComponent(code)}`);
  const o = d.data?.rows.find((r) => r.code === code) ?? null;
  usePrinted(kind, !!o, { ref: code });
  if (d.error) return <p role="alert">{d.error}</p>;
  if (!d.data) return <p>Reading the order…</p>;
  if (!o) return <p role="alert">No order {code} at the window.</p>;
  const surname = o.buyer.trim().split(/\s+/).slice(-1)[0] ?? o.buyer;
  const what = [...new Set(o.units.map((u) => u.what))]
    .map((w) => `${String(o.units.filter((u) => u.what === w).length)} × ${w}`)
    .join(' · ');
  return (
    <SheetPages
      doc={{
        size: 'card-landscape',
        title: kind === 'will-call' ? 'Will-call' : 'Ride bracelets',
        sub: `${kind === 'will-call' ? 'The ticket window' : 'Prepaid pickup · the ride booth'} · ${formatDate(new Date())}`,
        qr: o.code,
        name: kind === 'will-call' ? 'Will-call slip' : 'Bracelet envelope label',
        lead: (
          <>
            <p className="pr-big">{surname}</p>
            <p className="pr-sub">
              Order {o.code} · {what}
              {o.paid ? ' · paid' : ' · not paid'}
            </p>
          </>
        ),
        cols: ['mid', 'wide'],
        lines:
          kind === 'will-call'
            ? [
                { cells: ['Handed over', '____ : ____ by ______'] },
                { cells: ['ID or name match', 'Y / N'] },
              ]
            : [
                { cells: ['Redeemed', '____ : ____ station ___'] },
                { cells: ['Handed to', 'name on the record'] },
              ],
      }}
    />
  );
}
